import {firebasePlatformConfig} from './platformConfig';
import { readCollectionPages } from './collectionPages';
import { refreshRestSession } from './restSession';
import { auth } from './config';
import { ExpenseContext, isExpenseContext } from '../features/activity/expenseContext';

// Types (same as firestore.ts)
export interface Expense {
  currency?: string;
  manualKind?: string;
  recordOrigin?: string;
  _updateTime?: string;
  occurrencePayments?: Record<string, boolean>;
  contextOverride?: ExpenseContext | "";
  id?: string;
  name: string;
  amount: number;
  category: string;
  frequency: 'one-time' | 'weekly' | 'bi-weekly' | 'monthly';
  endDate?: Date | any;
  note?: string;
  date: Date | any;
  userId: string;
  createdAt?: any;
  // Payment status tracking
  paymentStatus?: 'neutral' | 'paid' | 'overdue';
  lastPaidMonth?: string; // YYYY-MM format
  paymentHistory?: Array<{ month: string; status: string }>; // For analytics
}

export interface Goal {
  id?: string;
  name: string;
  targetAmount: number;
  savedAmount: number;
  category: string;
  priority: 'low' | 'medium' | 'high';
  dueDate?: Date | any;
  addedDate?: Date | any;
  monthlyContribution: number;
  userId: string;
  createdAt?: any;
}

export interface CreditCard {
  id?: string;
  name: string;
  cardType: 'credit' | 'personal_loan' | 'car_loan';
  balance: number;
  limit: number;
  interestRate: number;
  monthlyPayment: number;
  dueDate?: string;
  lastStatementDate?: string;
  addedDate?: Date | any;
  isActive: boolean;
  isArchived?: boolean;
  archivedAt?: Date | any;
  additionalPaymentThisMonth?: number;
  userId: string;
  createdAt?: any;
}

export interface Category {
  id?: string;
  name: string;
  emoji: string;
  type: 'expense' | 'income';
  color: string;
  monthlyBudget?: number;
  spentThisMonth: number;
  lastUsed: string;
  isCustom: boolean;
  userId: string;
  createdAt?: any;
}

export interface IncomeSource {
  id?: string;
  name: string;
  amount: number;
  frequency: 'weekly' | 'bi-weekly' | 'monthly' | 'yearly';
  category: string;
  isActive: boolean;
  startDate?: Date | any;
  endDate?: Date | any;
  note?: string;
  userId: string;
  createdAt?: any;
}

export interface RecurringExpense {
  id?: string;
  name: string;
  amount: number;
  frequency: 'weekly' | 'bi-weekly' | 'monthly' | 'yearly';
  category: string;
  dueDate?: string;
  isActive: boolean;
  endDate?: Date | any;
  userId: string;
  createdAt?: any;
}

// Helper function to safely convert any date format to ISO string
function safeDateToISO(dateValue: any): string | null {
  if (dateValue instanceof Date) {
    return dateValue.toISOString();
  } else if (dateValue && typeof dateValue === 'object' && 'toDate' in dateValue) {
    // Firestore timestamp
    return (dateValue as any).toDate().toISOString();
  } else if (typeof dateValue === 'string') {
    // Already a string, validate it's a valid date
    const date = new Date(dateValue);
    if (!isNaN(date.getTime())) {
      return date.toISOString();
    } else {
      console.warn('Invalid date string, cannot convert to ISO:', dateValue);
      return null;
    }
  } else {
    console.warn('Unsupported date format, cannot convert to ISO:', dateValue);
    return null;
  }
}

// Helper function to get current ID token
export async function getIdToken(): Promise<string> {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    return refreshRestSession(auth.app.options.apiKey || '');
  }
  
  try {
    return await currentUser.getIdToken();
  } catch (error: any) {
    console.error('❌ [Firestore-REST] Error getting ID token:', error);
    
    // If token is expired or invalid, sign out the user
    if (error.code === 'auth/user-token-expired' || 
        error.code === 'auth/invalid-credential') {
      console.log('🔍 [Firestore-REST] Token expired, signing out user...');
      await auth.signOut();
    }
    
    throw new Error('Authentication failed');
  }
}

// Helper function to convert Firestore document to our interface
function decodeNestedValue(value: any): any {
  if (!value || typeof value !== 'object') return value;
  if ('mapValue' in value) return Object.fromEntries(Object.entries(value.mapValue?.fields || {}).map(([key, item]) => [key, decodeNestedValue(item)]));
  if ('arrayValue' in value) return (value.arrayValue?.values || []).map(decodeNestedValue);
  if ('stringValue' in value) return value.stringValue;
  if ('booleanValue' in value) return value.booleanValue;
  if ('integerValue' in value) return Number(value.integerValue);
  if ('doubleValue' in value) return Number(value.doubleValue);
  if ('nullValue' in value) return null;
  if ('timestampValue' in value) return new Date(value.timestampValue);
  return value;
}

function convertFirestoreDoc(doc: any, id: string): any {
  if (!doc.fields) {
    console.warn('🔍 [Firestore-REST] Document missing fields:', { doc, id });
    return null;
  }
  
  const data: any = { id, _updateTime: doc.updateTime };
  for (const [key, value] of Object.entries(doc.fields)) {
    if (value && typeof value === 'object' && 'stringValue' in value) {
      const stringValue = (value as any).stringValue;
      // Convert date-like strings to Date objects
      if (key === 'dueDate' || key === 'lastStatementDate' || key === 'endDate' || key === 'addedDate') {
        const dateValue = new Date(stringValue);
        data[key] = isNaN(dateValue.getTime()) ? stringValue : dateValue;
      } else {
        data[key] = stringValue;
      }
    } else if (value && typeof value === 'object' && 'integerValue' in value) {
      data[key] = parseInt((value as any).integerValue);
    } else if (value && typeof value === 'object' && 'doubleValue' in value) {
      data[key] = parseFloat((value as any).doubleValue);
    } else if (value && typeof value === 'object' && 'booleanValue' in value) {
      data[key] = (value as any).booleanValue;
    } else if (value && typeof value === 'object' && 'timestampValue' in value) {
      data[key] = new Date((value as any).timestampValue);
    } else {
      data[key] = decodeNestedValue(value);
    }
  }
  
  // Add safety check for Goal objects to ensure all required fields are present
  if (data.category && data.targetAmount !== undefined) {
    // This looks like a Goal object, ensure all required fields have default values
    if (data.name === undefined) data.name = 'Unnamed Goal';
    if (data.targetAmount === undefined) data.targetAmount = 0;
    if (data.savedAmount === undefined) data.savedAmount = 0;
    if (data.category === undefined) data.category = 'Other';
    if (data.priority === undefined) data.priority = 'medium';
    if (data.monthlyContribution === undefined) data.monthlyContribution = 0;
    if (data.userId === undefined) data.userId = '';
  }
  
  return data;
}

// Emulator support: set REACT_APP_USE_EMULATORS=true and REACT_APP_API_BASE_URL in .env.development.local
const USE_EMULATORS = process.env.REACT_APP_USE_EMULATORS === 'true';
const FUNCTIONS_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'https://us-central1-finflowtechs.cloudfunctions.net/api').replace(/\/$/, '');

// Base URL for Firestore REST API (emulator on port 8080 when REACT_APP_USE_EMULATORS=true)
const REST_PROJECT = firebasePlatformConfig(false).projectId;
export const FIRESTORE_BASE_URL = USE_EMULATORS
  ? `http://127.0.0.1:8080/v1/projects/${REST_PROJECT}/databases/(default)/documents`
  : `https://firestore.googleapis.com/v1/projects/${REST_PROJECT}/databases/(default)/documents`;

// Expense operations
export const expenseService = {
  async setOccurrencePaid(userId: string, expenseId: string, key: string, paid: boolean): Promise<void> {
    if (!userId || !expenseId || !/^d\d{4}_\d{2}_\d{2}$/.test(key) || (auth.currentUser && auth.currentUser.uid !== userId)) throw new Error('Invalid payment update.');
    const token = await getIdToken();
    const response = await fetch(`${FIRESTORE_BASE_URL}/users/${encodeURIComponent(userId)}/expenses/${encodeURIComponent(expenseId)}?updateMask.fieldPaths=occurrencePayments.${key}&currentDocument.exists=true&key=${auth.app.options.apiKey}`, {
      method:'PATCH', headers:{Authorization:`Bearer ${token}`, 'Content-Type':'application/json'},
      body:JSON.stringify({fields:{occurrencePayments:{mapValue:{fields:{[key]:{booleanValue:paid}}}}}})
    });
    if (!response.ok) throw new Error('Could not save payment status.');
  },
  async updateContext(userId: string, expenseId: string, context: ExpenseContext | ''): Promise<void> {
    if (!expenseId || !userId || (auth.currentUser && auth.currentUser.uid !== userId)) throw new Error('Sign in to update this expense.');
    if (context !== '' && !isExpenseContext(context)) throw new Error('Invalid expense context.');
    const idToken = await getIdToken();
    // A field mask preserves all existing financial fields and other user edits.
    const response = await fetch(`${FIRESTORE_BASE_URL}/users/${encodeURIComponent(userId)}/expenses/${encodeURIComponent(expenseId)}?updateMask.fieldPaths=contextOverride&currentDocument.exists=true&key=${auth.app.options.apiKey}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields: { contextOverride: { stringValue: context } } })
    });
    if (!response.ok) throw new Error('Could not save expense context.');
  },
  async getExpenses(userId: string): Promise<Expense[]> {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${FIRESTORE_BASE_URL}/users/${userId}/expenses?orderBy=date desc&key=${auth.app.options.apiKey}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await readCollectionPages(response, `${FIRESTORE_BASE_URL}/users/${userId}/expenses?orderBy=date desc&key=${auth.app.options.apiKey}`, { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" });
        const expenses = (data.documents || []).map((doc: any) => convertFirestoreDoc(doc, doc.name.split('/').pop())).filter((expense: any) => expense !== null);
        // Expenses loaded successfully
        return expenses as Expense[];
      } else {
        const errorText = await response.text();
        console.error('❌ [Firestore-REST] getExpenses failed:', response.status, errorText);
        throw new Error(`Firestore request failed: ${response.status}`);
      }
    } catch (error: any) {
      console.error('❌ [Firestore-REST] getExpenses error:', error);
      throw error;
    }
  },

  async addExpense(userId: string, expense: Omit<Expense, 'id' | 'userId' | 'createdAt'>): Promise<string> {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${FIRESTORE_BASE_URL}/users/${userId}/expenses?key=${auth.app.options.apiKey}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fields: {
            name: { stringValue: expense.name },
            amount: { doubleValue: expense.amount },
            category: { stringValue: expense.category },
            frequency: { stringValue: expense.frequency },
            date: { timestampValue: safeDateToISO(expense.date) || new Date().toISOString() },
            userId: { stringValue: userId },
            ...(expense.note && { note: { stringValue: expense.note } }),
            ...(expense.endDate && (() => {
              const timestampValue = safeDateToISO(expense.endDate);
              return timestampValue ? { endDate: { timestampValue } } : {};
            })())
          }
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.name.split('/').pop();
      } else {
        throw new Error(`Failed to add expense: ${response.status}`);
      }
    } catch (error: any) {
      console.error('❌ [Firestore-REST] addExpense error:', error);
      throw error;
    }
  },

  async updateExpense(userId: string, expenseId: string, updates: Partial<Expense>): Promise<void> {
    try {
      const idToken = await getIdToken();
      
      // Convert updates to plain values for Firebase Functions
      const updateFields: any = {};
      if (updates.name !== undefined) updateFields.name = updates.name;
      if (updates.amount !== undefined) updateFields.amount = updates.amount;
      if (updates.category !== undefined) updateFields.category = updates.category;
      if (updates.frequency !== undefined) updateFields.frequency = updates.frequency;
      if (updates.note !== undefined) updateFields.note = updates.note;
      if (updates.date !== undefined) {
        const timestampValue = safeDateToISO(updates.date);
        if (timestampValue) {
          updateFields.date = new Date(timestampValue);
        }
      }
      if (updates.endDate !== undefined) {
        const timestampValue = safeDateToISO(updates.endDate);
        if (timestampValue) {
          updateFields.endDate = new Date(timestampValue);
        }
      }
      // Payment status fields
      if (updates.paymentStatus !== undefined) updateFields.paymentStatus = updates.paymentStatus;
      if (updates.lastPaidMonth !== undefined) updateFields.lastPaidMonth = updates.lastPaidMonth;
      if (updates.paymentHistory !== undefined) updateFields.paymentHistory = updates.paymentHistory;
      
      // Use Firebase Functions to avoid CORS issues
      const url = `${FUNCTIONS_BASE_URL}/update-expense`;
      
      console.log('🔍 [Firestore-REST] updateExpense - URL:', url);
      console.log('🔍 [Firestore-REST] updateExpense - Request Body:', { uid: userId, expenseId, updates: updateFields });
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          uid: userId,
          expenseId,
          updates: updateFields
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [Firestore-REST] updateExpense failed:', response.status, errorText);
        throw new Error(`Failed to update expense: ${response.status}`);
      }
    } catch (error: any) {
      console.error('❌ [Firestore-REST] updateExpense error:', error);
      throw error;
    }
  },

  async deleteExpense(userId: string, expenseId: string): Promise<void> {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${FIRESTORE_BASE_URL}/users/${userId}/expenses/${expenseId}?key=${auth.app.options.apiKey}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete expense: ${response.status}`);
      }
    } catch (error: any) {
      console.error('❌ [Firestore-REST] deleteExpense error:', error);
      throw error;
    }
  }
};

// Goal operations
export const goalService = {
  async getGoals(userId: string): Promise<Goal[]> {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${FIRESTORE_BASE_URL}/users/${userId}/goals?key=${auth.app.options.apiKey}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await readCollectionPages(response, `${FIRESTORE_BASE_URL}/users/${userId}/goals?key=${auth.app.options.apiKey}`, { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" });
        console.log('🔍 [Firestore-REST] getGoals - Raw Firestore response:', data);
        
        const goals = (data.documents || []).map((doc: any) => {
          const converted = convertFirestoreDoc(doc, doc.name.split('/').pop());
          console.log('🔍 [Firestore-REST] getGoals - Converted goal:', converted);
          return converted;
        }).filter((goal: any) => goal !== null); // Filter out null values
        
        // Goals loaded successfully
        console.log('✅ [Firestore-REST] getGoals - Successfully loaded goals:', goals);
        return goals as Goal[];
      } else {
        const errorText = await response.text();
        console.error('❌ [Firestore-REST] getGoals failed:', response.status, errorText);
        throw new Error(`Firestore request failed: ${response.status}`);
      }
    } catch (error: any) {
      console.error('❌ [Firestore-REST] getGoals error:', error);
      throw error;
    }
  },

  async addGoal(userId: string, goal: Omit<Goal, 'id' | 'userId' | 'createdAt'>): Promise<string> {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${FIRESTORE_BASE_URL}/users/${userId}/goals?key=${auth.app.options.apiKey}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fields: {
            name: { stringValue: goal.name },
            targetAmount: { doubleValue: goal.targetAmount },
            savedAmount: { doubleValue: goal.savedAmount },
            category: { stringValue: goal.category },
            priority: { stringValue: goal.priority },
            monthlyContribution: { doubleValue: goal.monthlyContribution },
            userId: { stringValue: userId },
            ...(goal.addedDate && (() => {
              const timestampValue = safeDateToISO(goal.addedDate);
              return timestampValue ? { addedDate: { timestampValue } } : {};
            })()),
            ...(goal.dueDate && (() => {
              const timestampValue = safeDateToISO(goal.dueDate);
              return timestampValue ? { dueDate: { timestampValue } } : {};
            })())
          }
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.name.split('/').pop();
      } else {
        throw new Error(`Failed to add goal: ${response.status}`);
      }
    } catch (error: any) {
      console.error('❌ [Firestore-REST] addGoal error:', error);
      throw error;
    }
  },

  async updateGoal(userId: string, goalId: string, updates: Partial<Goal>): Promise<void> {
    try {
      const idToken = await getIdToken();
      const updateFields: any = {};
      
      if (updates.name !== undefined) updateFields.name = updates.name;
      if (updates.targetAmount !== undefined) updateFields.targetAmount = updates.targetAmount;
      if (updates.savedAmount !== undefined) updateFields.savedAmount = updates.savedAmount;
      if (updates.category !== undefined) updateFields.category = updates.category;
      if (updates.priority !== undefined) updateFields.priority = updates.priority;
      if (updates.monthlyContribution !== undefined) updateFields.monthlyContribution = updates.monthlyContribution;
      if (updates.dueDate !== undefined) {
        const timestampValue = safeDateToISO(updates.dueDate);
        if (timestampValue) {
          updateFields.dueDate = new Date(timestampValue);
        }
      }
      
      console.log('🔍 [Firestore-REST] updateGoal - Updating fields:', updateFields);
      
      // Use Firebase Functions to avoid CORS issues
      const url = `${FUNCTIONS_BASE_URL}/update-goal`;
      
      console.log('🔍 [Firestore-REST] Update URL:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          uid: userId,
          goalId,
          updates: updateFields
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [Firestore-REST] updateGoal failed:', response.status, errorText);
        throw new Error(`Failed to update goal: ${response.status}`);
      }
      
      console.log('✅ [Firestore-REST] updateGoal - Successfully updated goal:', goalId);
    } catch (error: any) {
      console.error('❌ [Firestore-REST] updateGoal error:', error);
      throw error;
    }
  },

  async deleteGoal(userId: string, goalId: string): Promise<void> {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${FIRESTORE_BASE_URL}/users/${userId}/goals/${goalId}?key=${auth.app.options.apiKey}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error(`Failed to delete goal: ${response.status}`);
      }
    } catch (error: any) {
      console.error('❌ [Firestore-REST] deleteGoal error:', error);
      throw error;
    }
  }
};

// Credit Card operations
export const creditCardService = {
  async getCreditCards(userId: string): Promise<CreditCard[]> {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${FIRESTORE_BASE_URL}/users/${userId}/creditCards?key=${auth.app.options.apiKey}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await readCollectionPages(response, `${FIRESTORE_BASE_URL}/users/${userId}/creditCards?key=${auth.app.options.apiKey}`, { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" });
        const creditCards = (data.documents || []).map((doc: any) => convertFirestoreDoc(doc, doc.name.split('/').pop())).filter((card: any) => card !== null);
        // Credit cards loaded successfully
        return creditCards as CreditCard[];
      } else {
        const errorText = await response.text();
        console.error('❌ [Firestore-REST] getCreditCards failed:', response.status, errorText);
        throw new Error(`Firestore request failed: ${response.status}`);
      }
    } catch (error: any) {
      console.error('❌ [Firestore-REST] getCreditCards error:', error);
      throw error;
    }
  },

  async addCreditCard(userId: string, card: Omit<CreditCard, 'id' | 'userId' | 'createdAt'>): Promise<string> {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${FIRESTORE_BASE_URL}/users/${userId}/creditCards?key=${auth.app.options.apiKey}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fields: {
            name: { stringValue: card.name },
            cardType: { stringValue: card.cardType },
            balance: { doubleValue: card.balance },
            limit: { doubleValue: card.limit },
            interestRate: { doubleValue: card.interestRate },
            monthlyPayment: { doubleValue: card.monthlyPayment },
            isActive: { booleanValue: card.isActive },
            userId: { stringValue: userId },
            ...(card.addedDate && (() => {
              const timestampValue = safeDateToISO(card.addedDate);
              return timestampValue ? { addedDate: { timestampValue } } : {};
            })()),
            ...(card.dueDate && { dueDate: { stringValue: card.dueDate } }),
            ...(card.lastStatementDate && { lastStatementDate: { stringValue: card.lastStatementDate } })
          }
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.name.split('/').pop();
      } else {
        throw new Error(`Failed to add credit card: ${response.status}`);
      }
    } catch (error: any) {
      console.error('❌ [Firestore-REST] addCreditCard error:', error);
      throw error;
    }
  },

  async updateCreditCard(userId: string, cardId: string, updates: Partial<CreditCard>): Promise<void> {
    try {
      const idToken = await getIdToken();
      const updateFields: any = {};
      
      console.log('🔍 [Firestore-REST] updateCreditCard called with updates:', updates);
      
      if (updates.name !== undefined) updateFields.name = updates.name;
      if (updates.cardType !== undefined) updateFields.cardType = updates.cardType;
      if (updates.balance !== undefined) updateFields.balance = updates.balance;
      if (updates.limit !== undefined) updateFields.limit = updates.limit;
      if (updates.interestRate !== undefined) updateFields.interestRate = updates.interestRate;
      if (updates.monthlyPayment !== undefined) updateFields.monthlyPayment = updates.monthlyPayment;
      if (updates.isActive !== undefined) updateFields.isActive = updates.isActive;
      if (updates.isArchived !== undefined) updateFields.isArchived = updates.isArchived;
      if (updates.archivedAt !== undefined) updateFields.archivedAt = updates.archivedAt;
      if (updates.dueDate !== undefined) updateFields.dueDate = updates.dueDate;
      if (updates.lastStatementDate !== undefined) updateFields.lastStatementDate = updates.lastStatementDate;
      
      console.log('🔍 [Firestore-REST] updateFields to be sent:', updateFields);
      
      // Use Firebase Functions to avoid CORS issues
      const url = `${FUNCTIONS_BASE_URL}/update-credit-card`;
      
      console.log('🔍 [Firestore-REST] Update URL:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          uid: userId,
          cardId,
          updates: updateFields
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [Firestore-REST] Update failed:', response.status, errorText);
        throw new Error(`Failed to update credit card: ${response.status}`);
      }
      
      // Log the response to see what was actually updated
      const responseData = await response.json();
      console.log('🔍 [Firestore-REST] Update response:', responseData);
    } catch (error: any) {
      console.error('❌ [Firestore-REST] updateCreditCard error:', error);
      throw error;
    }
  },

  async deleteCreditCard(userId: string, cardId: string): Promise<void> {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${FIRESTORE_BASE_URL}/users/${userId}/creditCards/${cardId}?key=${auth.app.options.apiKey}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete credit card: ${response.status}`);
      }
    } catch (error: any) {
      console.error('❌ [Firestore-REST] deleteCreditCard error:', error);
      throw error;
    }
  }
};

// Income Source operations
export const incomeSourceService = {
  async getIncomeSources(userId: string): Promise<IncomeSource[]> {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${FIRESTORE_BASE_URL}/users/${userId}/incomeSources?key=${auth.app.options.apiKey}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await readCollectionPages(response, `${FIRESTORE_BASE_URL}/users/${userId}/incomeSources?key=${auth.app.options.apiKey}`, { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" });
        const incomeSources = (data.documents || []).map((doc: any) => convertFirestoreDoc(doc, doc.name.split('/').pop())).filter((income: any) => income !== null);
        return incomeSources as IncomeSource[];
      } else {
        const errorText = await response.text();
        console.error('❌ [Firestore-REST] getIncomeSources failed:', response.status, errorText);
        throw new Error(`Firestore request failed: ${response.status}`);
      }
    } catch (error: any) {
      console.error('❌ [Firestore-REST] getIncomeSources error:', error);
      throw error;
    }
  },

  async getIncomeSource(userId: string, incomeId: string): Promise<IncomeSource | null> {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${FIRESTORE_BASE_URL}/users/${userId}/incomeSources/${incomeId}?key=${auth.app.options.apiKey}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        return convertFirestoreDoc(data, incomeId) as IncomeSource;
      } else {
        const errorText = await response.text();
        console.error('❌ [Firestore-REST] getIncomeSource failed:', response.status, errorText);
        return null;
      }
    } catch (error: any) {
      console.error('❌ [Firestore-REST] getIncomeSource error:', error);
      return null;
    }
  },

  async addIncomeSource(userId: string, income: Omit<IncomeSource, 'id' | 'userId' | 'createdAt'>): Promise<string> {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${FIRESTORE_BASE_URL}/users/${userId}/incomeSources?key=${auth.app.options.apiKey}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fields: {
            name: { stringValue: income.name },
            amount: { doubleValue: income.amount },
            category: { stringValue: income.category },
            frequency: { stringValue: income.frequency },
            isActive: { booleanValue: income.isActive },
            userId: { stringValue: userId },
            ...(income.startDate && (() => {
              const timestampValue = safeDateToISO(income.startDate);
              return timestampValue ? { startDate: { timestampValue } } : {};
            })()),
            ...(income.endDate && (() => {
              const timestampValue = safeDateToISO(income.endDate);
              return timestampValue ? { endDate: { timestampValue } } : {};
            })())
          }
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.name.split('/').pop();
      } else {
        throw new Error(`Failed to add income source: ${response.status}`);
      }
    } catch (error: any) {
      console.error('❌ [Firestore-REST] addIncomeSource error:', error);
      throw error;
    }
  },

  async updateIncomeSource(userId: string, incomeId: string, updates: Partial<IncomeSource>): Promise<void> {
    try {
      console.log('🚀 [Firestore-REST] updateIncomeSource START - userId:', userId, 'incomeId:', incomeId);
      
      const idToken = await getIdToken();
      console.log('🔑 [Firestore-REST] Got ID token successfully');
      
      // Prepare updates in the format expected by the Firebase function
      const updateFields: any = {};
      if (updates.name !== undefined) updateFields.name = updates.name;
      if (updates.amount !== undefined) updateFields.amount = updates.amount;
      if (updates.category !== undefined) updateFields.category = updates.category;
      if (updates.frequency !== undefined) updateFields.frequency = updates.frequency;
      if (updates.isActive !== undefined) updateFields.isActive = updates.isActive;
      if (updates.note !== undefined) updateFields.note = updates.note;
      if (updates.startDate !== undefined) {
        const timestampValue = safeDateToISO(updates.startDate);
        if (timestampValue) {
          updateFields.startDate = new Date(timestampValue);
        }
      }
      if (updates.endDate !== undefined) {
        const timestampValue = safeDateToISO(updates.endDate);
        if (timestampValue) {
          updateFields.endDate = new Date(timestampValue);
        }
      }
      
      console.log('🔍 [Firestore-REST] updateIncomeSource - Updating fields:', updateFields);
      
      // Use Firebase Functions to avoid CORS and updateMask issues
      const url = `${FUNCTIONS_BASE_URL}/update-income-source`;
      
      console.log('🔍 [Firestore-REST] Update URL:', url);
      console.log('🔍 [Firestore-REST] About to make fetch request to Firebase function...');
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          uid: userId,
          incomeId,
          updates: updateFields
        })
      });
      
      console.log('📡 [Firestore-REST] Fetch response received:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [Firestore-REST] updateIncomeSource failed:', response.status, errorText);
        throw new Error(`Failed to update income source: ${response.status}`);
      }
      
      console.log('✅ [Firestore-REST] updateIncomeSource - Successfully updated income source:', incomeId);
    } catch (error: any) {
      console.error('❌ [Firestore-REST] updateIncomeSource error:', error);
      console.error('❌ [Firestore-REST] Error stack:', error.stack);
      throw error;
    }
  },

  async deleteIncomeSource(userId: string, incomeId: string): Promise<void> {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${FIRESTORE_BASE_URL}/users/${userId}/incomeSources/${incomeId}?key=${auth.app.options.apiKey}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete income source: ${response.status}`);
      }
    } catch (error: any) {
      console.error('❌ [Firestore-REST] deleteIncomeSource error:', error);
      throw error;
    }
  },
};

// Category operations
export const categoryService = {
  async getCategories(userId: string): Promise<Category[]> {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${FIRESTORE_BASE_URL}/users/${userId}/categories?key=${auth.app.options.apiKey}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await readCollectionPages(response, `${FIRESTORE_BASE_URL}/users/${userId}/categories?key=${auth.app.options.apiKey}`, { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" });
        const categories = (data.documents || []).map((doc: any) => convertFirestoreDoc(doc, doc.name.split('/').pop())).filter((category: any) => category !== null);
        return categories as Category[];
      } else {
        const errorText = await response.text();
        console.error('❌ [Firestore-REST] getCategories failed:', response.status, errorText);
        throw new Error(`Firestore request failed: ${response.status}`);
      }
    } catch (error: any) {
      console.error('❌ [Firestore-REST] getCategories error:', error);
      throw error;
    }
  },

  async addCategory(userId: string, category: Omit<Category, 'id' | 'userId' | 'createdAt'>): Promise<string> {
    try {
      const idToken = await getIdToken();
      
      // First, check if a category with the same name already exists
      const existingCategories = await this.getCategories(userId);
      const existingCategory = existingCategories.find(cat => 
        cat.name.toLowerCase() === category.name.toLowerCase()
      );
      
      if (existingCategory) {
        console.log('🔍 [Firestore-REST] Category already exists:', existingCategory.name);
        throw new Error(`A category named "${category.name}" already exists. Please choose a different name.`);
      }
      
      // If no duplicate found, create new category
      const response = await fetch(`${FIRESTORE_BASE_URL}/users/${userId}/categories?key=${auth.app.options.apiKey}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fields: {
            name: { stringValue: category.name },
            emoji: { stringValue: category.emoji },
            type: { stringValue: category.type },
            color: { stringValue: category.color },
            spentThisMonth: { doubleValue: category.spentThisMonth },
            lastUsed: { stringValue: category.lastUsed },
            isCustom: { booleanValue: category.isCustom },
            userId: { stringValue: userId },
            ...(category.monthlyBudget && { monthlyBudget: { doubleValue: category.monthlyBudget } })
          }
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ [Firestore-REST] Category created successfully:', category.name);
        return data.name.split('/').pop();
      } else {
        throw new Error(`Failed to add category: ${response.status}`);
      }
    } catch (error: any) {
      console.error('❌ [Firestore-REST] addCategory error:', error);
      throw error;
    }
  },

  async updateCategory(userId: string, categoryId: string, updates: Partial<Category>): Promise<void> {
    try {
      const idToken = await getIdToken();
      const updateFields: any = {};
      
      if (updates.name !== undefined) updateFields.name = { stringValue: updates.name };
      if (updates.emoji !== undefined) updateFields.emoji = { stringValue: updates.emoji };
      if (updates.type !== undefined) updateFields.type = { stringValue: updates.type };
      if (updates.color !== undefined) updateFields.color = { stringValue: updates.color };
      if (updates.spentThisMonth !== undefined) updateFields.spentThisMonth = { doubleValue: updates.spentThisMonth };
      if (updates.lastUsed !== undefined) updateFields.lastUsed = { stringValue: updates.lastUsed };
      if (updates.isCustom !== undefined) updateFields.isCustom = { booleanValue: updates.isCustom };
      if (updates.monthlyBudget !== undefined) updateFields.monthlyBudget = { doubleValue: updates.monthlyBudget };
      
      // Use PATCH with updateMask to ensure only specified fields are updated
      const updateMask = Object.keys(updateFields).join(',');
      const url = `${FIRESTORE_BASE_URL}/users/${userId}/categories/${categoryId}?key=${auth.app.options.apiKey}&updateMask.fieldPaths=${updateMask}`;
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fields: updateFields
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update category: ${response.status}`);
      }
    } catch (error: any) {
      console.error('❌ [Firestore-REST] updateCategory error:', error);
      throw error;
    }
  },

  async deleteCategory(userId: string, categoryId: string): Promise<void> {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${FIRESTORE_BASE_URL}/users/${userId}/categories/${categoryId}?key=${auth.app.options.apiKey}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete category: ${response.status}`);
      }
    } catch (error: any) {
      console.error('❌ [Firestore-REST] deleteCategory error:', error);
      throw error;
    }
  }
};

// Recurring Expense operations
export const recurringExpenseService = {
  async getRecurringExpenses(userId: string): Promise<RecurringExpense[]> {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${FIRESTORE_BASE_URL}/users/${userId}/recurringExpenses?key=${auth.app.options.apiKey}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await readCollectionPages(response, `${FIRESTORE_BASE_URL}/users/${userId}/recurringExpenses?key=${auth.app.options.apiKey}`, { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" });
        const recurringExpenses = (data.documents || []).map((doc: any) => convertFirestoreDoc(doc, doc.name.split('/').pop())).filter((expense: any) => expense !== null);
        return recurringExpenses as RecurringExpense[];
      } else {
        const errorText = await response.text();
        console.error('❌ [Firestore-REST] getRecurringExpenses failed:', response.status, errorText);
        throw new Error(`Firestore request failed: ${response.status}`);
      }
    } catch (error: any) {
      console.error('❌ [Firestore-REST] getRecurringExpenses error:', error);
      throw error;
    }
  },

  async addRecurringExpense(userId: string, expense: Omit<RecurringExpense, 'id' | 'userId' | 'createdAt'>): Promise<string> {
    try {
      const idToken = await getIdToken();
      const response = await fetch(`${FIRESTORE_BASE_URL}/users/${userId}/recurringExpenses?key=${auth.app.options.apiKey}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fields: {
            name: { stringValue: expense.name },
            amount: { doubleValue: expense.amount },
            category: { stringValue: expense.category },
            frequency: { stringValue: expense.frequency },
            isActive: { booleanValue: expense.isActive },
            userId: { stringValue: userId },
            ...(expense.dueDate && { dueDate: { stringValue: expense.dueDate } }),
            ...(expense.endDate && (() => {
              const timestampValue = safeDateToISO(expense.endDate);
              return timestampValue ? { endDate: { timestampValue } } : {};
            })())
          }
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.name.split('/').pop();
      } else {
        throw new Error(`Failed to add recurring expense: ${response.status}`);
              }
      } catch (error: any) {
        console.error('❌ [Firestore-REST] addRecurringExpense error:', error);
        throw error;
      }
    },

    async deleteRecurringExpense(userId: string, expenseId: string): Promise<void> {
      try {
        const idToken = await getIdToken();
        const response = await fetch(`${FIRESTORE_BASE_URL}/users/${userId}/recurringExpenses/${expenseId}?key=${auth.app.options.apiKey}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to delete recurring expense: ${response.status}`);
        }
      } catch (error: any) {
        console.error('❌ [Firestore-REST] deleteRecurringExpense error:', error);
        throw error;
      }
    }
  }; 